/**
 * Retry safety for per-platform publish results stored on an AI social post.
 *
 * A failed platform may be re-sent only when we know the platform did not create the post. Two
 * facts are recorded at publish time from the live error: `retryable`, and `failureKind`, which
 * says why. `not_created` means the platform answered with an explicit rejection or the failure
 * happened before the request went out; `unknown` means the request may have reached the platform
 * but no answer came back, so a retry could post twice.
 */
export type PublishFailureKind = 'not_created' | 'unknown';

export type StoredPublishResult = {
  platform?: unknown;
  success?: unknown;
  retryable?: unknown;
  failureKind?: unknown;
  message?: unknown;
};

/** Messages that show the request itself never completed, so the platform's answer is unknown. */
const TRANSPORT_FAILURE_MESSAGE =
  /fetch failed|aborted|timed? ?out|socket hang up|ECONNRESET|ETIMEDOUT|EPIPE|network/i;

export function isRetryablePublishResult(result: StoredPublishResult | null | undefined) {
  if (!result || result.success !== false || typeof result.platform !== 'string') return false;
  if (typeof result.failureKind === 'string') return result.retryable === true;
  // Rows written before 2026-09-18 carry a flag derived from a message regex that was false for
  // almost every real rejection. A rejection means nothing was created, so for those rows only a
  // transport failure stays blocked.
  return !TRANSPORT_FAILURE_MESSAGE.test(typeof result.message === 'string' ? result.message : '');
}
